import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  HiOutlineCamera, 
  HiOutlineUpload, 
  HiOutlineSparkles, 
  HiOutlineX, 
  HiOutlineRefresh, 
  HiOutlineCheckCircle, 
  HiOutlineDocumentText, 
  HiOutlineLightningBolt,
  HiOutlineSwitchHorizontal,
  HiOutlineTruck,
  HiOutlineUser,
  HiOutlinePhone,
  HiOutlineOfficeBuilding,
  HiOutlineCube,
  HiOutlineHashtag,
  HiOutlineCurrencyRupee,
  HiOutlineCheck
} from 'react-icons/hi';
import { gateEntryAPI } from '../../services/api';
import { decodeQrFromImage } from '../../utils/qrScan';
import { TabBar, TabBtn } from './Tabs';
import toast from 'react-hot-toast';

// How often the live camera picture is checked for the bill's QR code
const QR_LIVE_SCAN_MS = 1200;

// Where each value came from (fieldStatus[key].status from the server)
const STATUS_BADGES = {
  exact: { text: '🔒 QR code', color: '#15803d', bg: 'rgba(34,197,94,0.14)', title: 'Read from the bill QR code — exact' },
  master: { text: '📇 Vendor master', color: '#15803d', bg: 'rgba(34,197,94,0.14)', title: 'From our vendor master for this GSTIN' },
  confirmed: { text: '✔ Both AI agree', color: '#15803d', bg: 'rgba(34,197,94,0.14)', title: 'Two AI models read the same value' },
  checksum: { text: '✔ GSTIN check', color: '#15803d', bg: 'rgba(34,197,94,0.14)', title: 'GSTIN check-digit is valid' },
  ocr_match: { text: '✔ OCR match', color: '#0f766e', bg: 'rgba(20,184,166,0.14)', title: 'AI models disagreed; the value found in the OCR text was used' },
  single: { text: 'AI read', color: '#1d4ed8', bg: 'rgba(59,130,246,0.12)', title: 'Read by one AI model' },
  review: { text: '⚠ Verify', color: '#b45309', bg: 'rgba(245,158,11,0.16)', title: 'AI models read different values — check the bill' },
  missing: { text: 'Not on bill', color: '#64748b', bg: 'rgba(100,116,139,0.12)', title: 'Not printed on the bill' }
};

// Inward form fields shown in the result, in form order
const RESULT_FIELDS = [
  { key: 'billNumber', label: 'Bill / Invoice No', icon: <HiOutlineHashtag /> },
  { key: 'billDate', label: 'Bill Date', icon: <HiOutlineDocumentText /> },
  { key: 'vendorName', label: 'Vendor / Supplier', icon: <HiOutlineOfficeBuilding /> },
  { key: 'vendorContact', label: 'Vendor Phone', icon: <HiOutlinePhone /> },
  { key: 'vendorGstin', label: 'Vendor GSTIN', icon: <HiOutlineHashtag /> },
  { key: 'materialType', label: 'Material', icon: <HiOutlineCube />, wide: true },
  { key: 'quantity', label: 'Quantity', icon: <HiOutlineCube /> },
  { key: 'vehicleNumber', label: 'Vehicle No', icon: <HiOutlineTruck /> },
  { key: 'driverName', label: 'Driver Name', icon: <HiOutlineUser /> },
  { key: 'driverPhone', label: 'Driver Phone', icon: <HiOutlinePhone /> },
  { key: 'eWayBillNumber', label: 'E-Way Bill No', icon: <HiOutlineHashtag /> },
  { key: 'totalAmount', label: 'Invoice Value', icon: <HiOutlineCurrencyRupee /> },
  { key: 'poNumber', label: 'Purchase Order', icon: <HiOutlineHashtag /> }
];

const formatFieldValue = (key, result) => {
  const value = result[key];
  if (key === 'quantity') return value ? `${value} ${result.unit || ''}`.trim() : '';
  if (key === 'totalAmount') return value ? `₹${Number(value).toLocaleString('en-IN')}` : '';
  if (key === 'billDate' && value) return value.split('-').reverse().join('-');
  return value ? String(value) : '';
};

const WebcamEBillScanner = ({ isOpen, onClose, onScanComplete }) => {
  const [activeTab, setActiveTab] = useState('upload'); // default to 'upload' or 'webcam'
  
  // Webcam state
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [capturedImage, setCapturedImage] = useState(null);
  
  // File Upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  
  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [lastMatchedPO, setLastMatchedPO] = useState(null);
  const [lastInvoiceUrl, setLastInvoiceUrl] = useState('');
  // { fieldsFound, source, aiEnabled, message, qrKind, fieldStatus, checks, scanId, savedTo, processingMs } from the last scan
  const [scanMeta, setScanMeta] = useState(null);

  // QR code on the bill, read live from the camera while the guard holds the bill up
  const [liveQrText, setLiveQrText] = useState('');
  const liveQrRef = useRef('');
  const qrBusyRef = useRef(false);

  // Initialize and list video devices
  const getVideoDevices = useCallback(async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return;
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = allDevices.filter(d => d.kind === 'videoinput');
      setDevices(videoInputs);
      if (videoInputs.length > 0 && !selectedDeviceId) {
        // Prefer back camera / environment if present
        const backCam = videoInputs.find(d => d.label.toLowerCase().includes('back') || d.label.toLowerCase().includes('environment'));
        setSelectedDeviceId(backCam ? backCam.deviceId : videoInputs[0].deviceId);
      }
    } catch (err) {
      console.warn('Could not enumerate video devices:', err);
    }
  }, [selectedDeviceId]);

  // Start Webcam Stream with high-definition constraints
  const startCamera = useCallback(async () => {
    setCameraError('');
    setCapturedImage(null);
    setScanResult(null);
    setLiveQrText('');
    liveQrRef.current = '';

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraError('Webcam access is not supported by your browser or environment. Please use file upload.');
      return;
    }

    try {
      // Stop any existing stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      // Highest resolution the webcam offers: small bill text and dense QR codes need it
      const constraints = {
        video: selectedDeviceId
          ? { deviceId: { exact: selectedDeviceId }, width: { ideal: 3840 }, height: { ideal: 2160 } }
          : { facingMode: 'environment', width: { ideal: 3840 }, height: { ideal: 2160 } }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setCameraActive(true);
      await getVideoDevices();
    } catch (err) {
      console.error('Camera access error:', err);
      let msg = 'Could not access webcam.';
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        msg = 'Camera permission was denied. Please allow camera permissions in browser settings.';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        msg = 'No camera device found on this system.';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        msg = 'Camera is already in use by another application.';
      }
      setCameraError(msg);
      setCameraActive(false);
    }
  }, [selectedDeviceId, getVideoDevices]);

  // Stop Webcam Stream
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  }, []);

  useEffect(() => {
    if (isOpen && activeTab === 'webcam') {
      startCamera();
    } else {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [isOpen, activeTab, startCamera, stopCamera]);

  // Look for the bill's QR code in the live picture
  useEffect(() => {
    if (!cameraActive || capturedImage || isProcessing) return undefined;
    const interval = setInterval(async () => {
      const video = videoRef.current;
      if (qrBusyRef.current || !video || !video.videoWidth) return;
      qrBusyRef.current = true;
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d').drawImage(video, 0, 0);
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.92));
        const text = await decodeQrFromImage(blob, { thorough: false });
        if (text) {
          liveQrRef.current = text;
          setLiveQrText(text);
        }
      } finally {
        qrBusyRef.current = false;
      }
    }, QR_LIVE_SCAN_MS);
    return () => clearInterval(interval);
  }, [cameraActive, capturedImage, isProcessing]);

  // Waits for a live QR decode in progress so two decodes never overlap
  const waitForQrIdle = async () => {
    for (let i = 0; i < 40 && qrBusyRef.current; i++) {
      await new Promise(r => setTimeout(r, 50));
    }
  };

  // Switch camera device
  const handleSwitchCamera = () => {
    if (devices.length <= 1) return;
    const currentIndex = devices.findIndex(d => d.deviceId === selectedDeviceId);
    const nextIndex = (currentIndex + 1) % devices.length;
    setSelectedDeviceId(devices[nextIndex].deviceId);
  };

  // Capture current video frame with high resolution
  const captureFrame = async () => {
    if (!videoRef.current || !canvasRef.current) return null;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth || 1920;
    canvas.height = video.videoHeight || 1080;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.92));
    setCapturedImage(dataUrl);
    stopCamera();
    return blob;
  };

  // Shows the result and tells the guard how much could actually be read
  const applyScanResponse = (res) => {
    const {
      data: extracted, matchedPO, invoiceUrl, fieldsFound = 0, source, aiEnabled, message, qrKind,
      fieldStatus = {}, checks = [], scanId, savedTo, processingMs
    } = res.data;
    setScanResult(extracted);
    setLastMatchedPO(matchedPO);
    setLastInvoiceUrl(invoiceUrl);
    setScanMeta({ fieldsFound, source, aiEnabled, message, qrKind, fieldStatus, checks, scanId, savedTo, processingMs });

    if (fieldsFound === 0) {
      toast.error(message || 'Could not read any details from this document.', { id: 'ebillScan', duration: 6000 });
    } else if (fieldsFound < 3) {
      toast(`⚠️ Only ${fieldsFound} of 5 key details could be read. Please check and fill the rest.`, { id: 'ebillScan', duration: 6000 });
    } else {
      toast.success(`✨ Document scanned! ${fieldsFound} of 5 key details extracted.`, { id: 'ebillScan' });
    }
  };

  const sendForScan = async (payload, loadingText) => {
    setIsProcessing(true);
    setScanResult(null);
    setScanMeta(null);
    try {
      toast.loading(loadingText, { id: 'ebillScan' });
      const res = await gateEntryAPI.scanEBill(payload);
      applyScanResponse(res);
      return true;
    } catch (err) {
      console.error('Scan Error:', err);
      toast.error(err.response?.data?.message || 'Failed to extract data from document', { id: 'ebillScan' });
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  // Process captured image from webcam (sent as a file so large photos aren't rejected)
  const handleScanWebcam = async () => {
    const blob = await captureFrame();
    if (!blob) {
      toast.error('Failed to capture frame from webcam');
      return;
    }
    const formData = new FormData();
    formData.append('file', blob, `ebill-webcam-${Date.now()}.jpg`);
    formData.append('source', 'webcam');
    await waitForQrIdle();
    if (liveQrRef.current) formData.append('qrText', liveQrRef.current);
    const ok = await sendForScan(formData, '🤖 AI reading the bill & QR code (10–20 sec)...');
    if (!ok) {
      setCapturedImage(null);
      startCamera();
    }
  };

  // Process file upload (DOCX, PDF, Images, Excel, CSV)
  const handleScanUploadedFile = async () => {
    if (!selectedFile) {
      toast.error('Please select a document, PDF, or image file first');
      return;
    }
    // The server reads the QR code (zoomed tiles) and the printed text with AI
    const formData = new FormData();
    formData.append('file', selectedFile);
    await sendForScan(formData, '🤖 AI reading the bill & QR code (10–20 sec)...');
  };

  // Process Quick Demo Sample Bill
  const handleSelectDemoSample = async (sampleType) => {
    await sendForScan({ sampleType }, `⚡ Loading sample ${sampleType.toUpperCase()} E-Way Bill...`);
  };

  // Paste an e-bill image copied from WhatsApp (Web/Desktop) with Ctrl+V
  const handlePaste = (e) => {
    const file = Array.from(e.clipboardData?.files || []).find(f => f.type.startsWith('image/') || f.type === 'application/pdf');
    if (!file) return;
    e.preventDefault();
    const named = file.name && file.name !== 'image.png'
      ? file
      : new File([file], `pasted-ebill-${Date.now()}.${file.type.split('/')[1] || 'png'}`, { type: file.type });
    setSelectedFile(named);
    setActiveTab('upload');
    toast.success('📋 Pasted e-bill image. Click "Extract & Pre-fill Form".');
  };

  // Apply to Form and Close
  const handleApplyToForm = () => {
    if (!scanResult) return;
    if (onScanComplete) {
      onScanComplete({
        ...scanResult,
        matchedPO: lastMatchedPO,
        invoiceUrl: lastInvoiceUrl || scanResult.invoiceUrl,
        fieldStatus: scanMeta?.fieldStatus || {}
      });
    }
    stopCamera();
    onClose();
  };

  // Guard picks which reading is right when the AI models disagreed
  const chooseAlternative = (key, value) => {
    setScanResult(prev => ({ ...prev, [key]: value }));
    setScanMeta(prev => ({
      ...prev,
      fieldStatus: { ...prev.fieldStatus, [key]: { ...prev.fieldStatus[key], value, status: 'confirmed', chosenByGuard: true } }
    }));
  };

  // Opens the organised JSON saved for this scan
  const handleViewScanJson = async () => {
    if (!scanMeta?.scanId) return;
    try {
      const res = await gateEntryAPI.getEBillScan(scanMeta.scanId);
      const blob = new Blob([JSON.stringify(res.data.data, null, 2)], { type: 'application/json' });
      window.open(URL.createObjectURL(blob), '_blank', 'noopener');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not open the scan record');
    }
  };

  if (!isOpen) return null;

  return (
    <div onPaste={handlePaste} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: '16px',
      animation: 'fadeIn 0.2s ease'
    }}>
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: '20px',
        width: '100%',
        maxWidth: '780px',
        maxHeight: '92vh',
        overflow: 'hidden',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 24px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          background: 'linear-gradient(135deg, rgba(59,130,246,0.1), rgba(99,102,241,0.04))'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <HiOutlineSparkles style={{ color: 'var(--primary)', fontSize: '1.4rem' }} />
              <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                AI Document & E-Way Bill Scanner
              </h3>
              <span style={{
                background: 'rgba(59, 130, 246, 0.15)',
                color: '#60a5fa',
                fontSize: '0.72rem',
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: '6px'
              }}>
                A to Z Auto-Fill
              </span>
            </div>
            <p style={{ margin: '3px 0 0', fontSize: '0.8rem', color: 'var(--text-dim)' }}>
              Scan via Live Webcam or upload Invoices, Challans & E-Way Bills (DOCX, PDF, Images) to auto-fill all Inward fields
            </p>
          </div>
          <button 
            onClick={() => { stopCamera(); onClose(); }}
            style={{
              background: 'transparent', border: 'none', color: 'var(--text-dim)',
              fontSize: '1.4rem', cursor: 'pointer', padding: '4px', borderRadius: '6px'
            }}
          >
            <HiOutlineX />
          </button>
        </div>

        {/* Tab Navigation */}
        <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)', flexShrink: 0, background: 'var(--bg-input)' }}>
          <TabBar style={{ marginBottom: 0 }}>
            {[
              { key: 'upload', label: '📁 Upload File / DOCX / PDF', icon: <HiOutlineUpload /> },
              { key: 'webcam', label: '📸 Live Webcam Scanner', icon: <HiOutlineCamera /> },
              { key: 'demo', label: '⚡ Demo Sample Bills', icon: <HiOutlineSparkles /> },
            ].map(t => (
              <TabBtn key={t.key} active={activeTab === t.key} onClick={() => setActiveTab(t.key)} icon={t.icon}>
                {t.label}
              </TabBtn>
            ))}
          </TabBar>
        </div>

        {/* Body Content */}
        <div style={{ padding: '24px', overflowY: 'auto', overflowX: 'hidden', flex: 1 }}>
          
          {/* TAB 1: FILE UPLOAD (DOCX, PDF, IMAGES, CSV) */}
          {activeTab === 'upload' && (
            <div>
              <div 
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                  e.preventDefault();
                  if (e.dataTransfer.files?.[0]) setSelectedFile(e.dataTransfer.files[0]);
                }}
                style={{
                  border: '2px dashed var(--primary)',
                  borderRadius: '16px',
                  padding: '36px 20px',
                  textAlign: 'center',
                  background: 'rgba(59, 130, 246, 0.04)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  style={{ display: 'none' }}
                  accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.txt,image/*"
                  onChange={e => setSelectedFile(e.target.files?.[0] || null)}
                />
                <HiOutlineDocumentText style={{ fontSize: '3.2rem', color: 'var(--primary)', marginBottom: '10px', opacity: 0.85 }} />
                
                {selectedFile ? (
                  <div>
                    <span style={{ fontWeight: 800, color: 'var(--text-primary)', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                      📄 {selectedFile.name}
                    </span>
                    <div style={{ fontSize: '0.8rem', color: '#22c55e', marginTop: '4px', fontWeight: 600 }}>
                      {(selectedFile.size / 1024).toFixed(1)} KB — Ready to Scan & Extract with AI
                    </div>
                  </div>
                ) : (
                  <div>
                    <h4 style={{ margin: '0 0 6px', fontSize: '1.05rem', color: 'var(--text-primary)', fontWeight: 700 }}>
                      Drag & Drop E-Way Bill, Material Challan, or Invoice
                    </h4>
                    <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-dim)' }}>
                      Supports Word (.docx, .doc), PDF, Excel (.xlsx), CSV, and Scanned Images (PNG, JPG, JPEG)
                    </p>
                    <p style={{ margin: '8px 0 0', fontSize: '0.8rem', color: 'var(--primary)', fontWeight: 600 }}>
                      📲 E-bill received on WhatsApp? Save the PDF/photo and upload it here, or copy the image and press Ctrl+V.
                    </p>
                  </div>
                )}
              </div>

              <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-dim)' }}>
                  ⚡ AI automatically extracts Vehicle, Driver, Vendor, PO, Items, Qty & Unit.
                </span>

                <button
                  className="btn btn-primary"
                  onClick={handleScanUploadedFile}
                  disabled={!selectedFile || isProcessing}
                  style={{
                    padding: '10px 24px',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'linear-gradient(135deg, #3b82f6, #2563eb)'
                  }}
                >
                  <HiOutlineLightningBolt /> {isProcessing ? 'AI Analyzing Document...' : 'Extract & Pre-fill Form'}
                </button>
              </div>
            </div>
          )}

          {/* TAB 2: WEBCAM SCANNER */}
          {activeTab === 'webcam' && (
            <div>
              {cameraError ? (
                <div style={{
                  padding: '28px', textAlign: 'center',
                  background: 'rgba(239, 68, 68, 0.08)', borderRadius: '16px',
                  border: '1px solid rgba(239, 68, 68, 0.3)'
                }}>
                  <p style={{ color: '#ef4444', fontWeight: 600, marginBottom: '16px' }}>{cameraError}</p>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                    <button className="btn btn-secondary btn-sm" onClick={startCamera}>
                      <HiOutlineRefresh /> Retry Camera
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={() => setActiveTab('upload')}>
                      <HiOutlineUpload /> Switch to File Upload
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  {/* Viewfinder Frame */}
                  <div style={{
                    position: 'relative',
                    width: '100%',
                    height: '340px',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    background: '#090d16',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid rgba(59, 130, 246, 0.4)',
                    boxShadow: 'inset 0 0 20px rgba(0,0,0,0.8)'
                  }}>
                    {capturedImage ? (
                      <img 
                        src={capturedImage} 
                        alt="Captured Document" 
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                      />
                    ) : (
                      <video
                        ref={videoRef}
                        playsInline
                        muted
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    )}

                    {/* QR status */}
                    {!capturedImage && cameraActive && (
                      <div style={{
                        position: 'absolute', top: '10px', left: '10px', zIndex: 2,
                        background: liveQrText ? 'rgba(22, 163, 74, 0.92)' : 'rgba(15, 23, 42, 0.75)',
                        color: '#fff', fontSize: '0.75rem', fontWeight: 700, padding: '5px 10px', borderRadius: '8px'
                      }}>
                        {liveQrText ? '✅ QR code read — hold steady and capture' : '🔍 Looking for the QR code on the bill...'}
                      </div>
                    )}

                    {/* Document Guide Overlay */}
                    {!capturedImage && (
                      <div style={{
                        position: 'absolute',
                        inset: '24px',
                        border: '2px dashed rgba(59, 130, 246, 0.6)',
                        borderRadius: '12px',
                        pointerEvents: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        padding: '12px',
                        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.3)'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div style={{ width: '20px', height: '20px', borderTop: '4px solid #3b82f6', borderLeft: '4px solid #3b82f6' }} />
                          <div style={{ width: '20px', height: '20px', borderTop: '4px solid #3b82f6', borderRight: '4px solid #3b82f6' }} />
                        </div>

                        {/* Scanner Laser Animation */}
                        <div style={{
                          height: '2px',
                          background: 'linear-gradient(90deg, transparent, #60a5fa, #3b82f6, #60a5fa, transparent)',
                          boxShadow: '0 0 8px #3b82f6',
                          animation: 'pulse 1.8s ease-in-out infinite'
                        }} />

                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div style={{ width: '20px', height: '20px', borderBottom: '4px solid #3b82f6', borderLeft: '4px solid #3b82f6' }} />
                          <div style={{ width: '20px', height: '20px', borderBottom: '4px solid #3b82f6', borderRight: '4px solid #3b82f6' }} />
                        </div>
                      </div>
                    )}

                    {/* Processing Overlay */}
                    {isProcessing && (
                      <div style={{
                        position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.75)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', gap: '12px'
                      }}>
                        <div className="spinner" style={{ width: '40px', height: '40px' }} />
                        <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>AI reading the bill with 2 models (10–20 sec)...</span>
                      </div>
                    )}
                  </div>

                  {/* Hidden Canvas for Frame Capture */}
                  <canvas ref={canvasRef} style={{ display: 'none' }} />

                  {/* Controls Toolbar */}
                  <div style={{
                    marginTop: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {devices.length > 1 && (
                        <button 
                          className="btn btn-secondary btn-sm"
                          onClick={handleSwitchCamera}
                          title="Switch Camera / Device"
                          style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                        >
                          <HiOutlineSwitchHorizontal /> Switch Camera ({devices.length})
                        </button>
                      )}
                      {capturedImage && (
                        <button 
                          className="btn btn-secondary btn-sm"
                          onClick={() => { setCapturedImage(null); startCamera(); }}
                          style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                        >
                          <HiOutlineRefresh /> Retake Photo
                        </button>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        className="btn btn-primary"
                        onClick={handleScanWebcam}
                        disabled={isProcessing || !cameraActive}
                        style={{
                          padding: '10px 24px',
                          fontSize: '0.92rem',
                          fontWeight: 700,
                          background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                          boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <HiOutlineCamera style={{ fontSize: '1.2rem' }} />
                        {isProcessing ? 'AI Processing Scan...' : 'Capture & Scan with AI'}
                      </button>
                    </div>
                  </div>

                  <p style={{ fontSize: '0.78rem', color: 'var(--text-dim)', textAlign: 'center', marginTop: '12px', marginBottom: 0 }}>
                    💡 Tip: Align physical E-Way bill or invoice inside the camera guide box for sharp optical character recognition.
                    First bring the QR code close until the green badge shows, then move back so the whole bill is in the frame, keep it flat and well lit, and capture.
                    Showing a WhatsApp e-bill on a phone? Zoom it in, turn screen brightness up and avoid glare. Uploading the WhatsApp file itself is more accurate.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* TAB 3: DEMO SAMPLE E-BILLS */}
          {activeTab === 'demo' && (
            <div>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                Select a standard pre-configured E-Way bill or supplier invoice to test immediate extraction & prefilling:
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {[
                  {
                    key: 'nylon',
                    title: 'Nylon 66 Granules E-Way Bill',
                    vendor: 'Apex Polymers & Synthetic Resins Pvt Ltd',
                    vehicle: 'MH-12-AB-8842',
                    qty: '500 KG',
                    color: '#3b82f6',
                    type: 'Raw Material'
                  },
                  {
                    key: 'pigment',
                    title: 'Black Masterbatch Invoice',
                    vendor: 'Supreme Masterbatch & Colorant Chem Pvt Ltd',
                    vehicle: 'DL-01-EA-5566',
                    qty: '150 KG',
                    color: '#a855f7',
                    type: 'Colorant Pigment'
                  },
                  {
                    key: 'packaging',
                    title: '5-Ply Corrugated Boxes E-Bill',
                    vendor: 'PackWell Heavy Cartons & Packaging',
                    vehicle: 'HR-26-DQ-9901',
                    qty: '250 Boxes',
                    color: '#eab308',
                    type: 'Packaging Material'
                  },
                  {
                    key: 'hardware',
                    title: 'Stainless Steel Fasteners Bill',
                    vendor: 'Metro Industrial Fasteners & Hardware',
                    vehicle: 'MH-14-GH-4321',
                    qty: '2,000 PCS',
                    color: '#10b981',
                    type: 'Hardware & Pins'
                  }
                ].map(demo => (
                  <div
                    key={demo.key}
                    onClick={() => handleSelectDemoSample(demo.key)}
                    style={{
                      padding: '16px',
                      borderRadius: '12px',
                      background: 'var(--bg-card-hover)',
                      border: '1px solid var(--border)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px'
                    }}
                    onMouseOver={e => e.currentTarget.style.borderColor = demo.color}
                    onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border)'}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                        {demo.title}
                      </span>
                      <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: '4px', background: `${demo.color}22`, color: demo.color, fontWeight: 700 }}>
                        {demo.type}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                      🏢 {demo.vendor}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '4px' }}>
                      <span>🚛 {demo.vehicle}</span>
                      <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>📦 {demo.qty}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Scan Results Card if extracted */}
          {scanResult && scanMeta && scanMeta.fieldsFound < 3 && (
            <div style={{
              marginTop: '24px',
              padding: '14px 16px',
              borderRadius: '12px',
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.4)',
              fontSize: '0.82rem',
              color: 'var(--text-primary)',
              lineHeight: 1.5
            }}>
              <strong style={{ color: '#d97706' }}>
                ⚠️ {scanMeta.fieldsFound === 0 ? 'No details could be read from this document.' : `Only ${scanMeta.fieldsFound} of 5 key details could be read.`}
              </strong>
              <div>
                {scanMeta.aiEnabled
                  ? 'Retake the photo closer and in better light, or upload the original PDF/image from WhatsApp.'
                  : 'AI reading is not configured on the server, so only basic text recognition (OCR) was used. Upload the original WhatsApp PDF for best results, or ask the admin to set GEMINI_API_KEY or OPENAI_API_KEY.'}
              </div>
            </div>
          )}

          {scanResult && (
            <div style={{
              marginTop: '24px',
              padding: '20px',
              borderRadius: '16px',
              background: 'rgba(34, 197, 94, 0.08)',
              border: '1px solid rgba(34, 197, 94, 0.35)',
              boxShadow: '0 8px 24px rgba(34, 197, 94, 0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <HiOutlineCheckCircle style={{ color: '#22c55e', fontSize: '1.4rem' }} />
                  <span style={{ fontWeight: 800, color: '#22c55e', fontSize: '1rem' }}>
                    AI Auto-Extracted Details (A to Z)
                  </span>
                  {scanMeta && (
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-dim)', fontWeight: 600 }}>
                      {scanMeta.fieldsFound}/5 key details · {scanMeta.source === 'ocr-regex' ? 'OCR' : 'AI'}
                    </span>
                  )}
                </div>

                <button
                  type="button"
                  onClick={handleApplyToForm}
                  style={{
                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 18px',
                    fontWeight: 800,
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)'
                  }}
                >
                  <HiOutlineCheck /> Apply & Pre-fill Inward Form
                </button>
              </div>

              {/* Every inward field with where its value came from */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', fontSize: '0.85rem' }}>
                {RESULT_FIELDS.map(({ key, label, icon, wide }) => {
                  const info = scanMeta?.fieldStatus?.[key];
                  const value = key === 'poNumber'
                    ? (scanResult.poNumber || lastMatchedPO?.poNumber || '')
                    : formatFieldValue(key, scanResult);
                  const badge = STATUS_BADGES[info?.status] || (value ? null : STATUS_BADGES.missing);
                  const needsReview = info?.status === 'review';
                  return (
                    <div key={key} style={{
                      background: 'var(--bg-card)', padding: '10px 12px', borderRadius: '8px', minWidth: 0,
                      border: `1px solid ${needsReview ? 'rgba(245,158,11,0.6)' : 'var(--border)'}`,
                      gridColumn: wide ? '1 / -1' : undefined
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px', marginBottom: '3px' }}>
                        <span style={{ color: 'var(--text-dim)', fontSize: '0.74rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {icon} {label}
                        </span>
                        {badge && (
                          <span title={badge.title} style={{ fontSize: '0.66rem', fontWeight: 700, color: badge.color, background: badge.bg, padding: '1px 6px', borderRadius: '4px', whiteSpace: 'nowrap' }}>
                            {badge.text}
                          </span>
                        )}
                      </div>
                      <strong style={{ color: value ? 'var(--text-primary)' : 'var(--text-muted)', fontSize: '0.9rem', wordBreak: 'break-word' }}>
                        {value || '—'}
                      </strong>
                      {needsReview && info.alternatives?.length > 1 && (
                        <div style={{ marginTop: '6px', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                          <span style={{ fontSize: '0.7rem', color: '#b45309' }}>Which is on the bill?</span>
                          {info.alternatives.map(alt => (
                            <button key={alt.value} type="button" onClick={() => chooseAlternative(key, alt.value)}
                              className="btn btn-secondary btn-sm" style={{ fontSize: '0.72rem', padding: '2px 8px' }}>
                              {String(alt.value)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {scanResult.materialDescription && (
                <div style={{ marginTop: '10px', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                  <strong>Items on bill:</strong> {scanResult.materialDescription}
                </div>
              )}

              {/* Cross-checks done on the bill */}
              {scanMeta?.checks?.length > 0 && (
                <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.78rem' }}>
                  {scanMeta.checks.map((c, i) => (
                    <div key={i} style={{ color: c.status === 'warning' ? '#b45309' : c.status === 'info' ? 'var(--text-dim)' : '#15803d' }}>
                      {c.status === 'warning' ? '⚠️' : c.status === 'info' ? 'ℹ️' : '✅'} {c.message}
                    </div>
                  ))}
                </div>
              )}

              {scanMeta?.scanId && (
                <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', fontSize: '0.74rem', color: 'var(--text-dim)' }}>
                  <span>
                    📄 Full scan data saved{scanMeta.savedTo ? `: ${scanMeta.savedTo}` : ''}
                    {scanMeta.processingMs ? ` · ${(scanMeta.processingMs / 1000).toFixed(1)}s` : ''}
                    {scanMeta.source ? ` · ${scanMeta.source}` : ''}
                  </span>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={handleViewScanJson} style={{ fontSize: '0.72rem', padding: '3px 10px' }}>
                    View scan JSON
                  </button>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default WebcamEBillScanner;
